import { ReactElement, useContext, useEffect, useMemo, useState } from "react"
import { useLocation } from "react-router-dom"

import { iconEdit } from "@wfp/icons"
import { Blockquote, Button, Loading, Modal } from "@wfp/ui"

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCompressAlt, faSave, faUpRightAndDownLeftFromCenter } from "@fortawesome/free-solid-svg-icons"

import useCommon from "../../../../hooks/useCommon"
import { DataUploadContext } from "../../../../providers/DataUploadContext"
import { useRestoreReportObsMutation } from "../../../../services/marketprices/reports"
import {
  useDeleteReportObsMutation,
  useGetMoMYoYMutation,
  useGetReportObsQuery,
  useUpdateCommodityPriceObservationMutation,
} from "../../../../services/marketprices/commodityPrice"

import { defaultOutlierFilters, getSections, MODA, outlierColumns } from "../../../../constants/validation"
import { IOutlierDetection, IReportObs, ObservationStatus } from "../../../../interfaces/markets"
import { countByStatus, detectOutliers, filterByStatus } from "../../../../utils/validation"

import FileUploader from "../../../Shared/ExcelUploader/FileUploader"
import ValidationDetails from "./ValidationDetails"
import ValidationKeys from "./ValidationKeys"
import ValidationSectionTab from "./ValidationTab"
import ValidationTable from "./ValidationTable"
import OutlierFilters from "./Outliers/OutlierFilters"
import DownloadExcel from "./DownloadExcel"

const VALIDATION_GUIDE_HIDDEN_KEY = "validationGuideHidden"

const Validation = (): ReactElement => {
  const { filters, setFilters, submitReport, isLoading, setDisabledNext, setObsWithOutliers } =
    useContext(DataUploadContext)
  const { showSnackBar } = useCommon()
  const { pathname, hash } = useLocation()

  const withUpload = !pathname.includes(MODA) && !hash.includes("review")

  const sections = useMemo(() => getSections(pathname), [pathname])

  const {
    data,
    isFetching: isLoadingReportObs,
    isError,
    isSuccess,
  } = useGetReportObsQuery(filters.reportId ?? 0, {
    skip: !filters.reportId,
  })
  const [updateObs, { isLoading: isUpdatingObs }] = useUpdateCommodityPriceObservationMutation()
  const [getMoMYoY] = useGetMoMYoYMutation()
  const [deleteReportObs] = useDeleteReportObsMutation()
  const [restoreReportObs] = useRestoreReportObsMutation()

  const [showFullscreen, setShowFullscreen] = useState<boolean>(false)
  const [uploadFile, setUploadFile] = useState<boolean>(false)
  const [selectedSection, setSelectedSection] = useState<number>(ObservationStatus.VALID)
  const [modal, setModal] = useState<{ type: "details" | "edit" | "delete" | null; isOpen: boolean }>({
    type: null,
    isOpen: false,
  })
  const [selectedItem, setSelectedItem] = useState<number>(0)
  const [selectedItems, setSelectedItems] = useState<number[]>([])
  const [updatedItems, setUpdatedItems] = useState<IReportObs[]>([])
  const [reportObs, setReportObs] = useState<IReportObs[]>([])
  const [outlierFilters, setOutlierFilters] = useState<{ MoM: number; YoY: number; showOutliers: boolean }>(
    defaultOutlierFilters,
  )
  const [showInfoModal, setShowInfoModal] = useState<boolean>(false)

  const mergedData = useMemo(() => {
    if (!updatedItems.length) return reportObs ?? []
    const updates = updatedItems.reduce((acc, item) => {
      acc[item.commodityPriceObservationId] = item
      return acc
    }, {} as Record<number, IReportObs>)

    return (reportObs ?? []).map(item => updates[item.commodityPriceObservationId] ?? item)
  }, [reportObs, updatedItems])

  const filteredData = useMemo(() => filterByStatus(mergedData, selectedSection), [mergedData, selectedSection])

  const withOutliers = useMemo(() => {
    if (selectedSection !== ObservationStatus.VALID) return filteredData
    return detectOutliers(filteredData)
  }, [filteredData, selectedSection])

  const currentSectionData = useMemo(() => {
    if (selectedSection !== ObservationStatus.VALID || !outlierFilters.showOutliers) {
      return withOutliers
    }

    return withOutliers.filter(
      item =>
        item.isOutlier ||
        (item.MoM !== undefined && Math.abs(item.MoM ?? 0) >= outlierFilters.MoM) ||
        (item.YoY !== undefined && Math.abs(item.YoY ?? 0) >= outlierFilters.YoY),
    )
  }, [withOutliers, outlierFilters, selectedSection])

  const currentSection = sections.find(s => s.statusId === selectedSection)

  const changeSection = (section: number): void => {
    setSelectedSection(section)
    setSelectedItems([])
  }

  const handleFileUpload = async (): Promise<void> => {
    try {
      await submitReport()
      setUploadFile(false)
    } catch {
      showSnackBar("Upload failed", "error")
    }
  }

  const handleFileChange = (files: File[] | null): void => {
    setFilters({ ...filters, file: files?.[0] ?? null })
  }

  const handleDetails = (item: IReportObs): void => {
    setSelectedItem(item.commodityPriceObservationId)
    setModal({ type: "details", isOpen: true })
  }

  const handleEdit = (item: IReportObs): void => {
    setSelectedItem(item.commodityPriceObservationId)
    setModal({ type: "edit", isOpen: true })
  }

  const handleDelete = (item: IReportObs): void => {
    setSelectedItem(item.commodityPriceObservationId)
    setModal({ type: "delete", isOpen: true })
  }

  const handleEditChange = (items: IReportObs[]): void => {
    setUpdatedItems(prev => {
      const updated = [...prev]

      items.forEach(item => {
        const index = updated.findIndex(
          element => element.commodityPriceObservationId === item.commodityPriceObservationId,
        )
        if (index !== -1) {
          updated[index] = item
        } else {
          updated.push(item)
        }
      })

      return updated
    })
    setDisabledNext(true)
    setSelectedItem(0)
    setSelectedItems([])
    setModal({ type: null, isOpen: false })
  }

  const onEditSubmit = async (): Promise<void> => {
    if (filters.reportId) {
      try {
        await updateObs(updatedItems).unwrap()

        setSelectedItems([])
        setUpdatedItems([])
        setDisabledNext(false)
        showSnackBar("Changes saved successfully")
      } catch (error) {
        console.error(error)
        showSnackBar("Failed to save changes", "error")
      }
    }
  }

  const onDeleteSubmit = async (): Promise<void> => {
    try {
      await deleteReportObs(selectedItem).unwrap()
      if (selectedItems.length) {
        setSelectedItems(prev => prev.filter(item => item !== selectedItem))
      }
      if (updatedItems.length) {
        setUpdatedItems(prev => prev.filter(item => item.commodityPriceObservationId !== selectedItem))
      }
      setSelectedItem(0)
      setModal({ type: null, isOpen: false })
    } catch (error) {
      console.error(error)
      showSnackBar("Failed to delete record", "error")
    }
  }

  const closeModal = (): void => {
    setSelectedItem(0)
    setModal({ type: null, isOpen: false })
  }

  const getFullScreenClassname = (): string => {
    return showFullscreen ? "absolute top-0 left-0 w-full h-full bg-white p-8" : ""
  }

  const handleCloseInfoModal = (): void => {
    setShowInfoModal(false)
  }

  const handleDontShowAgain = (): void => {
    localStorage.setItem(VALIDATION_GUIDE_HIDDEN_KEY, "true")
    setShowInfoModal(false)
  }

  const handleRestore = async (): Promise<void> => {
    if (!filters.reportId) return

    try {
      const result = await restoreReportObs(filters.reportId).unwrap()
      if (result?.observationsCreated === 0) showSnackBar("There are no observations to restore", "warning")
      else showSnackBar("Commodity price observations restored successfully")
    } catch (error) {
      console.error(error)
      showSnackBar("Failed to restore commodity price observations", "error")
    }
  }

  useEffect(() => {
    const isHidden = localStorage.getItem(VALIDATION_GUIDE_HIDDEN_KEY)
    if (!isHidden && withUpload) setShowInfoModal(true)
  }, [withUpload])

  useEffect(() => {
    ;(async () => {
      if (data?.length) {
        const payloadObj: Record<string, { seriesCombination: string; month: number; year: number }> = {}

        const updated = data.map(item => {
          const date = new Date(item.commodityPriceDate)
          if (!date || !item.seriesCombinationCode) return item
          const month = date.getMonth() + 1
          const year = date.getFullYear()

          const key = `${item.seriesCombinationCode}-${month}-${year}`
          payloadObj[key] = {
            seriesCombination: item.seriesCombinationCode ?? "",
            month,
            year,
          }

          return { ...item }
        })

        const payload = Object.values(payloadObj)

        if (!payload?.length) {
          setReportObs(updated)
          setObsWithOutliers(updated)
          setDisabledNext(true)
          return
        }

        try {
          const response = await getMoMYoY(payload).unwrap()

          if (response?.length) {
            const outlierObj = response.reduce((acc, item) => {
              const key = `${item.seriesCombination}-${item.month}-${item.year}`
              acc[key] = item
              return acc
            }, {} as Record<string, IOutlierDetection>)

            updated.forEach(item => {
              if (!item.seriesCombinationCode || !item.commodityPriceDate) return
              const date = new Date(item.commodityPriceDate)
              const month = date.getMonth() + 1
              const year = date.getFullYear()
              const key = `${item.seriesCombinationCode}-${month}-${year}`

              const outlier = outlierObj[key]
              if (outlier) {
                item.MoM = outlier.previousMonthPrice
                  ? ((item.commodityPriceValue - outlier.previousMonthPrice) / outlier.previousMonthPrice) * 100
                  : undefined
                item.YoY = outlier.previousYearPrice
                  ? ((item.commodityPriceValue - outlier.previousYearPrice) / outlier.previousYearPrice) * 100
                  : undefined
              }
            })
          }
        } catch (error) {
          console.error(error)
          showSnackBar("Failed to fetch MoM and YoY", "error")
        } finally {
          setReportObs(updated)
          setObsWithOutliers(updated)
        }
      } else {
        setReportObs([])
        setObsWithOutliers([])
      }
    })()
  }, [data])

  if (isError) {
    setDisabledNext(true)
    return (
      <Blockquote kind="error" withIcon className="p-4">
        An error occurred while fetching the data. Please try again later.
      </Blockquote>
    )
  }

  if (isLoadingReportObs || !data)
    return (
      <div className="min-h-[50vh] w-full flex justify-center items-center">
        <Loading active={isLoadingReportObs} small withOverlay={false} />
      </div>
    )

  if (isSuccess && data?.length === 0) {
    setDisabledNext(true)
    return (
      <Blockquote kind="warning" withIcon className="p-4">
        <p>No data found.</p>
        <p>You can restore the commodity prices observations by clicking the button below.</p>
        <Button onClick={handleRestore} className="mt-1" kind="primary">
          Restore commodity prices observations
        </Button>
      </Blockquote>
    )
  }

  return (
    <div className={getFullScreenClassname()} style={{ zIndex: showFullscreen ? 1001 : 1 }}>
      <div>
        <div className="flex justify-between flex-wrap gap-1">
          <div className="flex flex-wrap gap-0 text-sm">
            {sections.map(section => (
              <ValidationSectionTab
                key={section.statusId}
                section={section}
                isSelected={selectedSection === section.statusId}
                onClick={changeSection}
                count={countByStatus(reportObs ?? [], section.statusId)}
              />
            ))}
          </div>

          <div className="self-end p-1.5">
            {!!updatedItems.length && (
              <FontAwesomeIcon
                icon={faSave}
                color="orange"
                size="lg"
                onClick={onEditSubmit}
                className="mr-1.5 cursor-pointer animate-bounce"
                title="Save changes"
              />
            )}

            {withUpload && (
              <div className="relative inline-block mr-2">
                <Button onClick={() => setUploadFile(true)} className="text-xs h-6" small kind="accent">
                  Upload Excel File
                </Button>
              </div>
            )}

            <DownloadExcel data={reportObs} />

            <FontAwesomeIcon
              icon={showFullscreen ? faCompressAlt : faUpRightAndDownLeftFromCenter}
              color="var(--interactive-01)"
              className="ml-1.5 cursor-pointer"
              size="lg"
              title={showFullscreen ? "Exit fullscreen" : "Fullscreen"}
              onClick={() => setShowFullscreen(prev => !prev)}
            />
          </div>
        </div>
      </div>

      <div
        className="text-sm border-solid border border-gray-200 rounded-b rounded-tr p-4"
        style={{ height: showFullscreen ? "calc(100vh - 120px)" : "100%" }}
      >
        <Loading active={isUpdatingObs} small />
        {!!selectedItems?.length && currentSection?.edit && (
          <div className="mb-1">
            <Button
              onClick={() => setModal({ type: "edit", isOpen: true })}
              icon={iconEdit}
              iconReverse
              className="text-sm"
              small
              kind="secondary"
            >
              Edit {selectedItems.length} records
            </Button>
          </div>
        )}

        {sections.map(section => (
          <div
            key={section.statusId}
            className={selectedSection === section.statusId ? "block mb-1 text-gray-500" : "hidden"}
          >
            {section.description}
          </div>
        ))}

        {selectedSection === ObservationStatus.VALID && (
          <div className="mb-1 border border-gray-200 p-2 border-solid rounded-md">
            <OutlierFilters filters={outlierFilters} setFilters={setOutlierFilters} data={withOutliers} />
          </div>
        )}

        <ValidationTable
          data={currentSectionData}
          onDetails={currentSection?.details ? handleDetails : undefined}
          onEdit={currentSection?.edit ? handleEdit : undefined}
          onDelete={currentSection?.delete ? handleDelete : undefined}
          selectable={currentSection?.selectable}
          selectedItems={selectedItems}
          onSelectionChange={setSelectedItems}
          updatedItems={updatedItems}
          fullscreen={showFullscreen}
          columnsKey={`${outlierFilters.MoM}-${outlierFilters.YoY}`}
          extraColumns={
            selectedSection === ObservationStatus.VALID
              ? outlierColumns({
                  thresholdMoM: outlierFilters.MoM,
                  thresholdYoY: outlierFilters.YoY,
                })
              : []
          }
        />
      </div>

      {modal.type === "details" && modal.isOpen && (
        <ValidationDetails
          open={modal.isOpen}
          item={currentSectionData?.find(obs => obs.commodityPriceObservationId === selectedItem) ?? null}
          onClose={closeModal}
          onSubmit={handleEditChange}
        />
      )}

      {modal.type === "delete" && modal.isOpen && (
        <Modal
          open={modal.isOpen}
          modalHeading="Confirm Delete"
          primaryButtonText="Delete"
          secondaryButtonText="Cancel"
          onRequestSubmit={onDeleteSubmit}
          onRequestClose={closeModal}
          danger
        >
          Are you sure you want to delete the selected observation?
        </Modal>
      )}

      {modal.type === "edit" && modal.isOpen && (
        <ValidationKeys
          open={modal.isOpen}
          originalItems={
            reportObs?.filter(obs =>
              selectedItem
                ? obs.commodityPriceObservationId === selectedItem
                : selectedItems.includes(obs.commodityPriceObservationId),
            ) ?? []
          }
          items={currentSectionData.filter(obs =>
            selectedItem
              ? obs.commodityPriceObservationId === selectedItem
              : selectedItems.includes(obs.commodityPriceObservationId),
          )}
          onClose={closeModal}
          onSubmit={handleEditChange}
        />
      )}

      <Modal
        lazyLoad
        open={uploadFile}
        modalHeading="Upload File"
        secondaryButtonText="Close"
        primaryButtonText="Upload"
        primaryButtonDisabled={isLoading || !filters.file}
        onRequestSubmit={handleFileUpload}
        onRequestClose={() => setUploadFile(false)}
      >
        {isLoading ? (
          <div className="w-full h-[140px] flex justify-center items-center">
            <Loading withOverlay={false} small />
          </div>
        ) : (
          <div className="w-full">
            <FileUploader onChange={handleFileChange} files={filters.file ? [filters.file] : []} />
          </div>
        )}
      </Modal>

      <Modal
        lazyLoad
        modalHeading="Validation Guide"
        open={showInfoModal}
        primaryButtonText="Close"
        secondaryButtonText="Don't show again"
        onRequestSubmit={handleCloseInfoModal}
        onSecondarySubmit={handleDontShowAgain}
        onRequestClose={handleCloseInfoModal}
        className="leading-5"
      >
        <div className="space-y-3">
          <p>
            Review and correct any <b>errors</b> or <b>price outliers</b> in your Excel file, then click{" "}
            <span className="italic">Upload Excel File</span> to update the data.
          </p>
          <p>
            Use the <span className="italic">Download Validation Report</span> button to export a detailed summary of
            all detected issues for offline review and corrections.
          </p>
          <p>
            Click the <span className="italic">Fullscreen</span> icon for an expanded view and easier navigation.
          </p>
        </div>
      </Modal>
    </div>
  )
}

export default Validation
